import React, { useEffect, useState, useCallback } from "react";
import { API } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

// ── Constants ──────────────────────────────────────────────────────────────
const QUESTIONS = [
  { id: "q1", section: "Disconformidad y señales", text: "¿El cliente expresó algo negativo esta semana?",
    hint: "Mail, llamada, Slack o reunión. Si la respuesta es sí, anotarlo en notas.",
    options: [{ color: "red", label: "Sí → acción urgente" }, { color: "amber", label: "Señal leve" }, { color: "green", label: "No, todo bien" }] },
  { id: "q2", section: null, text: "¿Hay señales indirectas de disconformidad o desgaste?",
    hint: "Responde más lento, no aprueba piezas, bajó el entusiasmo, cancela calls.",
    options: [{ color: "amber", label: "Sí, hay señales" }, { color: "green", label: "No hay señales" }] },
  { id: "q3", section: "Entrega y riesgo", text: "¿Estamos entregando a tiempo?",
    hint: "Piezas, informes, validaciones. ¿Hay entregables del convenio no cumplidos sin que el cliente lo sepa?",
    options: [{ color: "red", label: "No, hay retrasos graves" }, { color: "amber", label: "Algunos retrasos" }, { color: "green", label: "Sí, a tiempo" }] },
  { id: "q4", section: null, text: "¿Hay riesgo de baja?",
    hint: "Contrato vence en < 45 días sin renovación, deuda pendiente, quejas sin resolver.",
    options: [{ color: "red", label: "Sí → riesgo alto" }, { color: "amber", label: "Riesgo medio" }, { color: "green", label: "Sin riesgo" }] },
  { id: "q5", section: "Percepción de valor", text: "¿El cliente entiende el valor de lo que hacemos?",
    hint: "No es si los números están bien — es si el cliente percibe que el servicio le sirve.",
    options: [{ color: "red", label: "No lo percibe" }, { color: "amber", label: "Parcialmente" }, { color: "green", label: "Sí, claramente" }] },
  { id: "q6", section: null, text: "¿El cliente percibe resultados o avance real?",
    hint: "Conformidad en la última iteración, llamada post-reunión, encuesta de satisfacción.",
    options: [{ color: "red", label: "No percibe avance" }, { color: "amber", label: "Dudoso" }, { color: "green", label: "Sí, lo ve claramente" }] },
  { id: "q7", section: "Oportunidad · Bono ML: 25% sobre horas vendidas", text: "¿Hay oportunidad de upselling?",
    hint: "Canal no contratado, mejora de web/CRO, influencers/UGC, necesidad no cubierta.",
    options: [{ color: "blue", label: "Sí → detallar" }, { color: "green", label: "No por ahora" }] },
];

const C = {
  red:   { label: "ROJO",     bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B", dot: "#EF4444", emoji: "🔴" },
  amber: { label: "AMARILLO", bg: "#FFFBEB", border: "#FCD34D", text: "#92400E", dot: "#F59E0B", emoji: "🟡" },
  green: { label: "VERDE",    bg: "#ECFDF5", border: "#6EE7B7", text: "#065F46", dot: "#10B981", emoji: "🟢" },
};

const OPT_COLORS = {
  red:   { bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B" },
  amber: { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E" },
  green: { bg: "#ECFDF5", border: "#10B981", text: "#065F46" },
  blue:  { bg: "#EFF6FF", border: "#3B82F6", text: "#1E40AF" },
};

function computeColor(answers) {
  const vals = Object.values(answers).map(a => a.color);
  if (vals.includes("red")) return "red";
  if (vals.includes("amber")) return "amber";
  return "green";
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

// ── Sub-components ─────────────────────────────────────────────────────────
function ColorBadge({ color, size = "sm" }) {
  const cfg = C[color] || C.green;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text,
      borderRadius: 20, padding: size === "sm" ? "2px 10px" : "4px 14px",
      fontSize: size === "sm" ? 11 : 13, fontWeight: 700, letterSpacing: ".04em",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

function QuestionRow({ q, answer, onAnswer }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {q.section && (
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-muted)", marginBottom: 6, paddingTop: 4 }}>
          {q.section}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", marginBottom: 4 }}>{q.text}</div>
      {q.hint && <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5 }}>{q.hint}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {q.options.map(opt => {
          const sel = answer?.color === opt.color && answer?.label === opt.label;
          const oc = OPT_COLORS[opt.color] || OPT_COLORS.green;
          return (
            <button key={opt.label} onClick={() => onAnswer(q.id, opt)}
              style={{
                fontSize: 12, fontWeight: 500, padding: "5px 14px", borderRadius: 20,
                border: `1px solid ${sel ? oc.border : "var(--border)"}`,
                background: sel ? oc.bg : "var(--bg-surface)",
                color: sel ? oc.text : "var(--text-secondary)",
                cursor: "pointer", transition: "all .15s", fontFamily: "inherit",
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Semaforo() {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("session");

  // Session tab state
  const [allClients, setAllClients] = useState([]);
  const [session, setSession] = useState(null);
  const [loadingSess, setLoadingSess] = useState(true);
  const [addingClient, setAddingClient] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // entry being edited
  const [selClientId, setSelClientId] = useState("");
  const [answers, setAnswers] = useState({});
  const [notas, setNotas] = useState("");
  const [upsellText, setUpsellText] = useState("");
  const [savingEntry, setSavingEntry] = useState(false);
  const [minutes, setMinutes] = useState("");
  const [savingMin, setSavingMin] = useState(false);
  const [report, setReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [cancelingSession, setCancelingSession] = useState(false);

  // History tab state
  const [hFilterClient, setHFilterClient] = useState("");
  const [hFilterFrom, setHFilterFrom] = useState("");
  const [hFilterTo, setHFilterTo] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [hLoading, setHLoading] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load clients + latest session on mount
  useEffect(() => {
    API.get("/api/clients").then(r =>
      setAllClients(r.data.slice().sort((a, b) => a.name.localeCompare(b.name, "es")))
    ).catch(() => {});
    API.get("/api/semaforo/sessions?limit=1")
      .then(r => {
        if (r.data.length > 0 && r.data[0].status !== "sent") {
          return API.get(`/api/semaforo/sessions/${r.data[0].id}`);
        }
        return null;
      })
      .then(r => { if (r) { setSession(r.data); setMinutes(r.data.minutes || ""); setReport(r.data.report_text || ""); } })
      .catch(() => {})
      .finally(() => setLoadingSess(false));
  }, []);

  const handleNewSession = async () => {
    try {
      const r = await API.post("/api/semaforo/sessions");
      setSession(r.data);
      setMinutes("");
      setReport("");
    } catch { toast("Error al crear sesión", "error"); }
  };

  const handleAnswer = (qId, opt) => {
    setAnswers(prev => ({ ...prev, [qId]: { color: opt.color, label: opt.label } }));
  };

  const resetForm = () => {
    setAddingClient(false);
    setEditingEntry(null);
    setSelClientId("");
    setAnswers({});
    setNotas("");
    setUpsellText("");
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setSelClientId(String(entry.client_id));
    setAnswers(entry.answers || {});
    setNotas(entry.notas || "");
    setUpsellText(entry.upsell_text || "");
    setAddingClient(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSaveEntry = async () => {
    if (!selClientId) { toast("Seleccioná un cliente", "error"); return; }
    const answered = Object.keys(answers).length;
    if (answered < 7) { toast(`Respondé las ${7 - answered} preguntas restantes`, "error"); return; }
    setSavingEntry(true);
    try {
      const r = await API.post(`/api/semaforo/sessions/${session.id}/entries`, {
        client_id: selClientId, answers, notas, upsell_text: upsellText
      });
      setSession(prev => {
        const existing = prev.entries.find(e => e.client_id === parseInt(selClientId));
        const entries = existing
          ? prev.entries.map(e => e.client_id === parseInt(selClientId) ? r.data : e)
          : [...prev.entries, r.data];
        return { ...prev, entries };
      });
      resetForm();
      toast(editingEntry ? "Cliente actualizado" : "Cliente guardado", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al guardar", "error");
    } finally { setSavingEntry(false); }
  };

  const handleSinNovedades = async () => {
    if (!selClientId) { toast("Seleccioná un cliente primero", "error"); return; }
    // Auto-fill all green answers
    const greenAnswers = Object.fromEntries(
      QUESTIONS.map(q => {
        const greenOpt = q.options.find(o => o.color === "green");
        return [q.id, { color: greenOpt.color, label: greenOpt.label }];
      })
    );
    setSavingEntry(true);
    try {
      const r = await API.post(`/api/semaforo/sessions/${session.id}/entries`, {
        client_id: selClientId,
        answers: greenAnswers,
        notas: "Sin novedades esta semana",
        upsell_text: "",
      });
      setSession(prev => {
        const existing = prev.entries.find(e => e.client_id === parseInt(selClientId));
        const entries = existing
          ? prev.entries.map(e => e.client_id === parseInt(selClientId) ? r.data : e)
          : [...prev.entries, r.data];
        return { ...prev, entries };
      });
      resetForm();
      toast("Sin novedades guardado ✓", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al guardar", "error");
    } finally { setSavingEntry(false); }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await API.delete(`/api/semaforo/sessions/${session.id}/entries/${entryId}`);
      setSession(prev => ({ ...prev, entries: prev.entries.filter(e => e.id !== entryId) }));
      toast("Cliente eliminado", "success");
    } catch { toast("Error al eliminar", "error"); }
  };

  const handleSaveMinutes = async () => {
    setSavingMin(true);
    try {
      await API.patch(`/api/semaforo/sessions/${session.id}/minutes`, { minutes });
      setSession(prev => ({ ...prev, minutes }));
      toast("Minuta guardada", "success");
    } catch { toast("Error al guardar minuta", "error"); }
    finally { setSavingMin(false); }
  };

  const handleGenerateReport = async () => {
    if (!session.minutes && !minutes) { toast("Agregá la minuta antes de generar el reporte", "error"); return; }
    setGenerating(true);
    try {
      // Save minutes first if changed
      if (minutes !== session.minutes) await API.patch(`/api/semaforo/sessions/${session.id}/minutes`, { minutes });
      const r = await API.post(`/api/semaforo/sessions/${session.id}/generate-report`);
      setReport(r.data.report_text);
      setSession(prev => ({ ...prev, status: "report_generated", report_text: r.data.report_text }));
      toast("Reporte generado", "success");
    } catch (err) {
      // La llamada a Claude puede tardar 30-60s y Nginx/red puede cortar la conexión
      // antes de que llegue la respuesta. El backend igual completa y guarda el reporte.
      // Recargamos la sesión para ver si el reporte ya está en la DB.
      try {
        const reloaded = await API.get(`/api/semaforo/sessions/${session.id}`);
        if (reloaded.data.report_text) {
          setReport(reloaded.data.report_text);
          setSession(reloaded.data);
          setMinutes(reloaded.data.minutes || minutes);
          toast("Reporte generado", "success");
          return;
        }
      } catch (_) { /* ignorar error secundario */ }
      toast(err.response?.data?.error || "Error al generar reporte. Intentá de nuevo.", "error");
    } finally { setGenerating(false); }
  };

  const handleSaveReport = async () => {
    try {
      await API.patch(`/api/semaforo/sessions/${session.id}/report`, { report_text: report });
      setSession(prev => ({ ...prev, report_text: report }));
      toast("Reporte guardado", "success");
    } catch { toast("Error al guardar reporte", "error"); }
  };

  const handleSendSlack = async () => {
    setSending(true);
    try {
      if (report !== session.report_text) await API.patch(`/api/semaforo/sessions/${session.id}/report`, { report_text: report });
      await API.post(`/api/semaforo/sessions/${session.id}/send-slack`);
      setSession(prev => ({ ...prev, status: "sent" }));
      toast("¡Enviado a Slack!", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al enviar a Slack", "error");
    } finally { setSending(false); }
  };

  const handleCancelSession = async () => {
    if (!window.confirm("¿Cancelar esta sesión? Se eliminará junto con todos los clientes evaluados.")) return;
    setCancelingSession(true);
    try {
      await API.delete(`/api/semaforo/sessions/${session.id}`);
      setSession(null);
      setMinutes("");
      setReport("");
      resetForm();
      toast("Sesión cancelada", "success");
    } catch (err) {
      toast(err.response?.data?.error || "Error al cancelar la sesión", "error");
    } finally { setCancelingSession(false); }
  };

  const handleSearchHistory = useCallback(async () => {
    setHLoading(true);
    setDetailEntry(null);
    try {
      const params = new URLSearchParams();
      if (hFilterClient) params.set("client_id", hFilterClient);
      if (hFilterFrom) params.set("from", hFilterFrom);
      if (hFilterTo) params.set("to", hFilterTo);
      const r = await API.get(`/api/semaforo/history?${params}`);
      setHistoryRows(r.data);
    } catch { toast("Error al cargar historial", "error"); }
    finally { setHLoading(false); }
  }, [hFilterClient, hFilterFrom, hFilterTo, toast]);

  useEffect(() => { if (tab === "history") handleSearchHistory(); }, [tab]);

  const handleViewDetail = async (entryId) => {
    setLoadingDetail(true);
    try {
      const r = await API.get(`/api/semaforo/entries/${entryId}`);
      setDetailEntry(r.data);
    } catch { toast("Error al cargar detalle", "error"); }
    finally { setLoadingDetail(false); }
  };

  const existingClientIds = new Set((session?.entries || []).map(e => e.client_id));
  // Solo clientes activos pueden sumarse a una evaluación nueva — allClients
  // se deja sin filtrar porque el selector de historial (más abajo) sí
  // necesita poder filtrar evaluaciones pasadas de clientes ya desactivados.
  const availableClients = allClients.filter(c => c.status === "active" && !existingClientIds.has(c.id));
  const currentColor = Object.keys(answers).length === 7 ? computeColor(answers) : null;
  const sessionSent = session?.status === "sent";

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 className="page-title">🚦 Semáforo Comercial</h1>
          {session && <StatusBadge status={session.status} />}
        </div>
        <p className="page-subtitle">Evaluación semanal de clientes · solo para líderes</p>
      </div>

      {/* Tabs */}
      <div className="page-body">
        <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid var(--border)", paddingBottom: 0 }}>
          {[["session","📋 Sesión semanal"], ["history","🗂 Historial"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{
                padding: "8px 18px", fontSize: 13, fontWeight: 600, border: "none",
                borderBottom: tab === id ? "2px solid var(--accent)" : "2px solid transparent",
                background: "none", color: tab === id ? "var(--accent)" : "var(--text-muted)",
                cursor: "pointer", fontFamily: "inherit", transition: "color .15s",
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── TAB: SESIÓN ── */}
        {tab === "session" && (
          <div>
            {loadingSess ? (
              <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" /></div>
            ) : !session ? (
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🚦</div>
                <p style={{ color: "var(--text-muted)", marginBottom: 20, fontSize: 14 }}>No hay una sesión activa. Iniciá una nueva para comenzar el semáforo de esta semana.</p>
                <button className="btn btn-primary" onClick={handleNewSession}>+ Nueva sesión semanal</button>
              </div>
            ) : (
              <div>
                {/* Session header */}
                <div className="card" style={{ padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                      Semana del {fmtDate(session.session_date)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{session.entries?.length || 0} clientes evaluados</div>
                  </div>
                  {!sessionSent && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={handleCancelSession}
                      disabled={cancelingSession}
                      style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                      title="Cancelar y eliminar esta sesión"
                    >
                      {cancelingSession ? "Cancelando..." : "✕ Cancelar sesión"}
                    </button>
                  )}
                </div>

                {/* Entries list */}
                {(session.entries || []).length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", marginBottom: 10 }}>Clientes evaluados</div>
                    {session.entries.map(entry => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        onDelete={!sessionSent ? handleDeleteEntry : null}
                        onEdit={!sessionSent ? handleEditEntry : null}
                      />
                    ))}
                  </div>
                )}

                {/* Add client form */}
                {!sessionSent && (
                  <div className="card" style={{ marginBottom: 24 }}>
                    {!addingClient ? (
                      <button className="btn btn-secondary" onClick={() => setAddingClient(true)}
                        style={{ width: "100%", justifyContent: "center" }}
                        disabled={availableClients.length === 0}>
                        + Agregar cliente {availableClients.length === 0 ? "(todos agregados)" : ""}
                      </button>
                    ) : (
                      <div style={{ padding: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>
                              {editingEntry ? `Editando: ${editingEntry.client_name}` : "Nuevo cliente"}
                            </span>
                            {currentColor && <ColorBadge color={currentColor} />}
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            {!editingEntry && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleSinNovedades}
                                disabled={!selClientId || savingEntry}
                                title="Guardar como verde — sin novedades esta semana"
                              >
                                🟢 Sin novedades
                              </button>
                            )}
                            <button className="btn btn-ghost btn-sm" onClick={resetForm}>✕ Cancelar</button>
                          </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", display: "block", marginBottom: 6 }}>Cliente</label>
                          {editingEntry ? (
                            <div style={{ fontSize: 14, fontWeight: 500, padding: "8px 12px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", display: "inline-block", color: "var(--text-primary)" }}>
                              {editingEntry.client_name}
                            </div>
                          ) : (
                            <select className="select" value={selClientId} onChange={e => setSelClientId(e.target.value)} style={{ maxWidth: 320 }}>
                              <option value="">Seleccioná un cliente...</option>
                              {availableClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          )}
                        </div>

                        {QUESTIONS.map(q => (
                          <QuestionRow key={q.id} q={q} answer={answers[q.id]} onAnswer={handleAnswer} />
                        ))}

                        {answers.q7?.color === "blue" && (
                          <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", display: "block", marginBottom: 6 }}>💡 ¿Qué servicio o mejora podés proponer?</label>
                            <input className="input" value={upsellText} onChange={e => setUpsellText(e.target.value)} placeholder="Ej: LinkedIn Ads B2B, SEO, TikTok orgánico..." />
                          </div>
                        )}

                        <div style={{ marginBottom: 16 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", display: "block", marginBottom: 6 }}>Notas adicionales</label>
                          <textarea className="textarea" rows={3} value={notas} onChange={e => setNotas(e.target.value)} placeholder="Contexto adicional, comentarios del PM..." />
                        </div>

                        <button className="btn btn-primary" onClick={handleSaveEntry} disabled={savingEntry}>
                          {savingEntry ? "Guardando..." : "Guardar cliente"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Minutes section */}
                {!sessionSent && (session.entries || []).length > 0 && (
                  <div className="card" style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>📝 Minuta de la reunión</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>Pegá el texto de la minuta generada por Gemini (o un link). Se usa para generar el reporte con IA.</div>
                    <textarea className="textarea" rows={6} value={minutes} onChange={e => setMinutes(e.target.value)} placeholder="Pegá aquí el texto completo de la minuta o un link al documento..." />
                    <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                      <button className="btn btn-secondary btn-sm" onClick={handleSaveMinutes} disabled={savingMin}>
                        {savingMin ? "Guardando..." : "Guardar minuta"}
                      </button>
                      <button className="btn btn-primary" onClick={handleGenerateReport} disabled={generating || !minutes}>
                        {generating ? "Generando con IA..." : "✨ Generar reporte"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Report section */}
                {report && (
                  <div className="card" style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>🚀 Reporte para Slack</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Revisá y editá antes de enviar. Usa formato Slack mrkdwn (*negrita*, _cursiva_).</div>
                      </div>
                      {!sessionSent && <button className="btn btn-secondary btn-sm" onClick={handleSaveReport}>Guardar cambios</button>}
                    </div>
                    <textarea className="textarea" rows={18} value={report} onChange={e => setReport(e.target.value)} disabled={sessionSent}
                      style={{ fontFamily: "monospace", fontSize: 12 }} />
                    {!sessionSent && (
                      <div style={{ marginTop: 12 }}>
                        <button className="btn btn-primary" onClick={handleSendSlack} disabled={sending}>
                          {sending ? "Enviando..." : "📣 Confirmar y enviar a Slack"}
                        </button>
                      </div>
                    )}
                    {sessionSent && (
                      <div style={{ marginTop: 10, padding: "8px 14px", background: "#ECFDF5", borderRadius: "var(--radius-sm)", fontSize: 12, color: "#065F46", fontWeight: 500 }}>
                        ✅ Enviado a #semaforo-comercial el {fmtDate(session.slack_sent_at || session.session_date)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: HISTORIAL ── */}
        {tab === "history" && (
          <div>
            {/* Filters */}
            <div className="card" style={{ padding: "16px 20px", marginBottom: 20 }}>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: isMobile ? "stretch" : "flex-end" }}>
                <div style={{ flex: isMobile ? "none" : "1 1 200px" }}>
                  <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", display: "block", marginBottom: 6 }}>Cliente</label>
                  <select className="select" value={hFilterClient} onChange={e => setHFilterClient(e.target.value)} style={{ width: "100%" }}>
                    <option value="">Todos los clientes</option>
                    {allClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div style={{ flex: isMobile ? "none" : "0 1 160px" }}>
                  <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", display: "block", marginBottom: 6 }}>Desde</label>
                  <input className="input" type="date" value={hFilterFrom} onChange={e => setHFilterFrom(e.target.value)} style={{ width: "100%" }} />
                </div>
                <div style={{ flex: isMobile ? "none" : "0 1 160px" }}>
                  <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", display: "block", marginBottom: 6 }}>Hasta</label>
                  <input className="input" type="date" value={hFilterTo} onChange={e => setHFilterTo(e.target.value)} style={{ width: "100%" }} />
                </div>
                <button className="btn btn-primary btn-sm" onClick={handleSearchHistory} disabled={hLoading} style={isMobile ? { width: "100%", justifyContent: "center" } : {}}>
                  {hLoading ? "Buscando..." : "Buscar"}
                </button>
              </div>
            </div>

            {/* Detail view */}
            {detailEntry && (
              <div className="card" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDetailEntry(null)}>← Volver</button>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{detailEntry.client_name}</div>
                  <ColorBadge color={detailEntry.color} />
                  <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: "auto" }}>{fmtDate(detailEntry.session_date)}</span>
                </div>
                {detailEntry.pm_name && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>PM: {detailEntry.pm_name}</div>}
                {QUESTIONS.map(q => {
                  const ans = detailEntry.answers?.[q.id];
                  if (!ans) return null;
                  const oc = OPT_COLORS[ans.color] || OPT_COLORS.green;
                  return (
                    <div key={q.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>{q.text}</div>
                      <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 12, background: oc.bg, border: `1px solid ${oc.border}`, color: oc.text }}>{ans.label}</span>
                    </div>
                  );
                })}
                {detailEntry.upsell_text && <div style={{ fontSize: 13, marginBottom: 10 }}><strong>💡 Upselling:</strong> {detailEntry.upsell_text}</div>}
                {detailEntry.notas && <div style={{ fontSize: 13, marginBottom: 10 }}><strong>Notas:</strong> {detailEntry.notas}</div>}
              </div>
            )}

            {/* History table */}
            {!detailEntry && (
              hLoading ? (
                <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" /></div>
              ) : historyRows.length === 0 ? (
                <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)", fontSize: 14 }}>No hay registros para los filtros seleccionados.</div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", minWidth: 480, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--border)" }}>
                          {["Fecha", "Cliente", "PM", "Semáforo", ""].map(h => (
                            <th key={h} style={{ padding: "11px 18px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {historyRows.map(row => (
                          <tr key={row.entry_id} style={{ borderBottom: "1px solid var(--border)" }}>
                            <td style={{ padding: "12px 18px", fontSize: 13, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{fmtDate(row.session_date)}</td>
                            <td style={{ padding: "12px 18px", fontSize: 13, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap" }}>{row.client_name}</td>
                            <td style={{ padding: "12px 18px", fontSize: 13, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{row.pm_name || "—"}</td>
                            <td style={{ padding: "12px 18px" }}><ColorBadge color={row.color} /></td>
                            <td style={{ padding: "12px 18px", textAlign: "right" }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => handleViewDetail(row.entry_id)} disabled={loadingDetail} style={{ whiteSpace: "nowrap" }}>Ver →</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── EntryCard ──────────────────────────────────────────────────────────────
function EntryCard({ entry, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="card" style={{ marginBottom: 10, padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <ColorBadge color={entry.color} />
        <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)", flex: 1 }}>{entry.client_name}</span>
        {entry.pm_name && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>PM: {entry.pm_name}</span>}
        <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(p => !p)} style={{ fontSize: 12 }}>
          {expanded ? "▲ Ocultar" : "▼ Ver respuestas"}
        </button>
        {onDelete && (
          <button className="btn btn-ghost btn-sm" onClick={() => onDelete(entry.id)} style={{ fontSize: 12, color: "var(--danger)" }}>🗑</button>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
          {QUESTIONS.map(q => {
            const ans = entry.answers?.[q.id];
            if (!ans) return null;
            const oc = OPT_COLORS[ans.color] || OPT_COLORS.green;
            return (
              <div key={q.id} style={{ marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)", flex: 1, paddingTop: 2 }}>{q.text}</span>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 12, background: oc.bg, border: `1px solid ${oc.border}`, color: oc.text, whiteSpace: "nowrap", flexShrink: 0 }}>{ans.label}</span>
              </div>
            );
          })}
          {entry.upsell_text && <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6 }}>💡 {entry.upsell_text}</div>}
          {entry.notas && <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>📝 {entry.notas}</div>}
          {onEdit && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => onEdit(entry)}>✏️ Editar respuestas</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    open:              { label: "En curso",          bg: "#EFF6FF", color: "#1E40AF" },
    report_generated:  { label: "Reporte generado",  bg: "#FFFBEB", color: "#92400E" },
    sent:              { label: "Enviado a Slack",    bg: "#ECFDF5", color: "#065F46" },
  }[status] || { label: status, bg: "var(--bg-surface)", color: "var(--text-muted)" };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}
